import { useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import AccordionStepRegisterOffice from './accordion';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { transformRequestRegisterOffice } from 'practice/utils/transform-request-step-register-office';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeData } from 'shell/models/practice';
import { transformRegisterOffice } from 'practice/utils/transform-step-register-office';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { validationSchema } from './constants';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { TypeRegister } from 'practice/constants/type-register';
import { TypePractice } from 'practice/constants/type-practice';
import { MilanMuncipality } from 'practice/constants/muncipality';
import { defaultChoicePickerState } from 'shell/components/shared/choice-date/constants';
import { Message } from 'shell/components/shared/search-messages';
import ModalReason from '../shared/modal-reason';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';
import { TypeInitiative } from 'practice/constants/practice-management-step';

export interface AccountholderCitizenshipVM {
  stateDescription?: string;
  stateCode?: string;
}

interface StepRegisterOfficeProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  idFamily?: string;
  intestatarioIsAnpr?: boolean;
  setIntestatarioIsAnpr: (value: boolean) => void;
  setTipoIniziativa: (value?: TypeInitiative) => void;
  iniziativa?: TypeInitiative;
  setStepBozza: (value: number) => void;
  stepBozza?: number;
  setCodOperatorePratica: (value: string) => void;
  codOperatorePratica?: string;
  onNext: () => void;
  maxActiveStep?: number;
  disabledAll?: boolean;
  setDisabledAll: (bool: boolean) => void;
  setIsDisabledCancel: (value: boolean) => void;
  setStatoPratica: (value: string) => void;
  ForPracticeTemporary?: boolean;
  setFromPerson: (value?: TypeInitiative) => void;
  setStepRegisterOfficeState: (values: RegisterOfficeVM) => void;
}

const StepRegisterOfficeComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  idFamily,
  intestatarioIsAnpr,
  setIntestatarioIsAnpr,
  setTipoIniziativa,
  iniziativa,
  setStepBozza,
  stepBozza,
  setCodOperatorePratica,
  codOperatorePratica,
  onNext,
  maxActiveStep,
  disabledAll,
  setDisabledAll,
  setIsDisabledCancel,
  setStatoPratica,
  ForPracticeTemporary = false,
  setFromPerson,
  setStepRegisterOfficeState,
}: StepRegisterOfficeProps): React.ReactElement => {
  const [pratica] = useHttpFetch<HttpResponse<PracticeData>, RegisterOfficeVM>(
    `${__PRACTICE_API_URL__}/pratiche/pratica/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformRegisterOffice,
    }
  );

  useEffect(() => {
    const typeInitiative = (pratica.data as RegisterOfficeVM)?.typeInitiative;

    // Se irreperibile per censimento e non è stata ancora salvata la bozza apre con iniziativa in ufficio
    if (isUntraceableCensus && !typeInitiative && !Boolean(iniziativa)) {
      setTipoIniziativa(TypeInitiative.idUfficio);
    }

    if (typeInitiative && !Boolean(iniziativa)) {
      setTipoIniziativa(typeInitiative);
    }
  }, [(pratica.data as RegisterOfficeVM)?.typeInitiative]);

  useEffect(() => {
    const isAire = (pratica.data as RegisterOfficeVM)?.isAire || false;

    if (isAire && !intestatarioIsAnpr) {
      setIntestatarioIsAnpr(isAire);
    }
  }, [(pratica.data as RegisterOfficeVM)?.isAire]);

  useEffect(() => {
    const step = (pratica.data as RegisterOfficeVM)?.stepBozza;

    if (step && !Boolean(stepBozza)) {
      setStepBozza(step);
    }
  }, [(pratica.data as RegisterOfficeVM)?.stepBozza]);

  useEffect(() => {
    const state = (pratica.data as RegisterOfficeVM)?.statoPratica;

    if (state && state !== '0') {
      setDisabledAll(state !== '0');
    }
  }, [(pratica.data as RegisterOfficeVM)?.statoPratica]);

  useEffect(() => {
    const state = (pratica.data as RegisterOfficeVM)?.statoPratica;

    if (state) {
      setStatoPratica(state);
    }
  }, [(pratica.data as RegisterOfficeVM)?.statoPratica]);

  useEffect(() => {
    const codOperatore = (pratica.data as RegisterOfficeVM)?.codOperatorePratica;

    if (codOperatore && !Boolean(codOperatorePratica)) {
      setCodOperatorePratica(codOperatore);
    }
  }, [(pratica.data as RegisterOfficeVM)?.codOperatorePratica]);

  useEffect(() => {
    const disabled = (pratica.data as RegisterOfficeVM)?.disabledCancel;

    setIsDisabledCancel(disabled || false);
  }, [(pratica.data as RegisterOfficeVM)?.disabledCancel]);

  const updateData = useHttpClient<HttpResponse<PracticeData>>(
    `${__PRACTICE_API_URL__}/pratiche/residenza/apertura`,
    {
      options: { method: 'POST' },
    }
  );

  const updateDataStep = useEventCallback((values: RegisterOfficeVM) => {
    const data = transformRequestRegisterOffice(values, maxActiveStep || 0, {
      typePractice,
      codeEnte,
      yearId,
      progressive,
      typeRegister,
    });

    return updateData({ data }) as ResponsePromise<HttpResponse<PracticeData>>;
  });

  const onSubmit = useEventCallback((values: RegisterOfficeVM) => {
    setStepRegisterOfficeState(values);
    if (disabledAll || iniziativa === TypeInitiative.ElixForm) {
      onNext();
      return;
    }

    updateDataStep(values).then((res) => {
      if (res?.data?.payload?.flagMinoreSolo && !modalReason.hasOpened) {
        setOpenModalReason({
          open: true,
          motivo: res.data.payload?.operazioneVOList?.find((el) => el.tipoOperazione === '00')
            ?.noteOperazione,
          warnings: [
            {
              id: 1,
              value:
                'Attenzione per poter aprire una pratica per un cittadino solo minorenne è necessario inserire il motivo',
            },
          ],
        });
        return;
      }
      toast.success('Operazione eseguita con successo');
      // reset new person
      filterTable.setValues(undefined);

      onNext();
    });
  });

  const filterTable = useFilterTable<RegisterOfficeVM>();

  const initialValues: RegisterOfficeVM | undefined = useMemo(() => {
    if (
      (filterTable.values?.applicantData || filterTable.values?.accountholderData) &&
      filterTable.values?.currentStep === 0
    ) {
      const practiceData = (pratica.data || {}) as RegisterOfficeVM;

      let applicantIsAccountholder = true;

      if (practiceData.applicantData) {
        applicantIsAccountholder = false;
      } else if (filterTable.values.applicantData && filterTable.values.applicantData?.generality) {
        applicantIsAccountholder = false;
      }

      return {
        ...practiceData,
        applicantIsAccountholder,
        typeInitiative:
          practiceData?.typeInitiative || iniziativa || TypeInitiative.idPersonaLegittima,
        applicantData: practiceData?.applicantData
          ? undefined
          : {
              ...(filterTable.values.applicantData || {}),
              birthdayChoice:
                filterTable.values.applicantData?.generality?.birthChoiceDate ||
                defaultChoicePickerState,
              citizenship: filterTable.values.applicantData?.citizenship,
              generality: filterTable.values.applicantData?.generality
                ? {
                    ...filterTable.values.applicantData?.generality,
                    birthdayChoice:
                      filterTable.values.applicantData?.generality?.birthChoiceDate ||
                      defaultChoicePickerState,
                  }
                : undefined,
              identificationDocument: filterTable.values.applicantData?.identificationDocument,
            },
        accountholderData: practiceData?.accountholderData
          ? undefined
          : {
              ...(filterTable.values?.accountholderData || {}),
              birthdayChoice:
                filterTable.values.accountholderData?.generality?.birthChoiceDate ||
                defaultChoicePickerState,
              citizenship: filterTable.values?.accountholderData?.citizenship,
              generality: filterTable.values.accountholderData?.generality
                ? {
                    ...filterTable.values.accountholderData?.generality,
                    birthdayChoice:
                      filterTable.values.accountholderData?.generality?.birthChoiceDate ||
                      defaultChoicePickerState,
                  }
                : undefined,
              identificationDocument: filterTable.values?.accountholderData?.identificationDocument
                ? {
                    ...(filterTable.values?.accountholderData?.identificationDocument || {}),
                    documentNumber:
                      filterTable.values?.accountholderData?.identificationDocument
                        ?.documentNumber || '',
                    issuedDate:
                      filterTable.values?.accountholderData?.identificationDocument?.issuedDate ||
                      null,
                    municipality:
                      filterTable.values?.accountholderData?.identificationDocument?.municipality ||
                      null,
                    // placeForeign
                    // isForeign
                  }
                : undefined,
            },
      };
    }
    return (pratica.data || undefined) as RegisterOfficeVM;
  }, [pratica.data, filterTable.values, iniziativa]);

  const isUnavailable = useMemo(() => {
    return typePractice === TypePractice.Unavailable || typeRegister === TypeRegister.Unavailable;
  }, [typePractice, typeRegister]);

  const isUntraceableCensus = useMemo(() => {
    return (
      typePractice === TypePractice.UntraceableCensus ||
      typeRegister === TypeRegister.UntraceableCensus
    );
  }, [typePractice, typeRegister]);

  const formContext = useFormik<RegisterOfficeVM>({
    initialValues: {
      typePractice,
      applicantIsAccountholder:
        isUnavailable || isUntraceableCensus
          ? false
          : initialValues?.typeInitiative === TypeInitiative.idUfficio
          ? true
          : initialValues?.applicantIsAccountholder || false,
      ...(initialValues || {}),
      typeInitiative:
        (initialValues || {})?.typeInitiative || iniziativa || TypeInitiative.idPersonaLegittima,
      accountholderData: initialValues?.accountholderData
        ? {
            ...(initialValues?.accountholderData || {}),
            fiscalCode: '',
            generality: initialValues?.accountholderData?.generality
              ? {
                  ...(initialValues?.accountholderData?.generality || {}),
                  gender: initialValues?.accountholderData?.generality?.gender || undefined,
                  name: initialValues?.accountholderData?.generality?.name || undefined,
                  surname: initialValues?.accountholderData?.generality?.surname || undefined,
                }
              : undefined,
            identificationDocument: initialValues?.accountholderData?.identificationDocument
              ? {
                  documentNumber:
                    initialValues?.accountholderData?.identificationDocument?.documentNumber ||
                    undefined,
                  issuedDate:
                    initialValues?.accountholderData?.identificationDocument?.issuedDate ||
                    undefined,
                  isForeign:
                    initialValues?.accountholderData?.identificationDocument?.isForeign || false,
                  municipality:
                    initialValues?.accountholderData?.identificationDocument?.municipality || null,
                  placeForeign:
                    initialValues?.accountholderData?.identificationDocument?.placeForeign ||
                    undefined,
                }
              : undefined,
          }
        : {
            fiscalCode: '',
          },
      applicantData: initialValues?.applicantData
        ? {
            ...(initialValues?.applicantData || {}),
            generality: initialValues?.applicantData?.generality
              ? {
                  ...(initialValues?.applicantData?.generality || {}),
                  gender: initialValues?.applicantData?.generality?.gender || undefined,
                  name: initialValues?.applicantData?.generality?.name || undefined,
                  surname: initialValues?.applicantData?.generality?.surname || undefined,
                }
              : undefined,
            identificationDocument: initialValues?.applicantData?.identificationDocument
              ? {
                  documentNumber:
                    initialValues?.applicantData?.identificationDocument?.documentNumber ||
                    undefined,
                  issuedDate:
                    initialValues?.applicantData?.identificationDocument?.issuedDate || undefined,
                  isForeign:
                    initialValues?.applicantData?.identificationDocument?.isForeign || false,
                  municipality:
                    initialValues?.applicantData?.identificationDocument?.municipality || null,
                  placeForeign:
                    initialValues?.applicantData?.identificationDocument?.placeForeign || undefined,
                }
              : undefined,
          }
        : undefined,
      residenceTemporary: {
        italian: {
          municipality: MilanMuncipality,
        },
      },
    },
    enableReinitialize: true,
    validationSchema:
      iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm
        ? undefined
        : validationSchema,
    onSubmit,
  });

  const disabled: boolean = useMemo(() => {
    if (((pratica.data as RegisterOfficeVM)?.stepBozza || 0) > 0) {
      return true;
    }

    if (disabledAll) {
      return true;
    }

    if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
      // ANPR o ELIXFORM
      return true;
    }

    if (codOperatorePratica === 'BATCH') {
      return true;
    }

    return false;
  }, [(pratica.data as RegisterOfficeVM)?.stepBozza, codOperatorePratica, disabledAll, iniziativa]);

  const [modalReason, setOpenModalReason] = useState<{
    open: boolean;
    warnings: Message[];
    motivo?: string;
    hasOpened?: boolean;
  }>({
    open: false,
    warnings: [],
  });

  useEffect(() => {
    const fromPersonValue = formContext.values.typeInitiative;

    setFromPerson(fromPersonValue);
  }, [formContext.values.typeInitiative]);

  useEffect(() => {
    if (disabled) {
      setStepRegisterOfficeState(formContext.initialValues);
    }
  }, [formContext.initialValues, disabled]);

  return (
    <Fragment>
      {pratica.isLoading || pratica.pristine || pratica.hasErrors ? undefined : (
        <AccordionStepRegisterOffice
          formContext={formContext}
          initialValues={initialValues}
          codeEnte={codeEnte}
          yearId={yearId}
          typeRegister={typeRegister}
          progressive={progressive}
          typePractice={typePractice}
          idFamily={idFamily}
          disabled={disabled}
          disabledAnpr={iniziativa === TypeInitiative.Anpr}
          disabledElix={iniziativa === TypeInitiative.ElixForm}
          iniziativa={iniziativa}
          setTipoIniziativa={setTipoIniziativa}
          onNext={onNext}
          setIntestatarioIsAnpr={setIntestatarioIsAnpr}
          ForPracticeTemporary={ForPracticeTemporary}
        />
      )}
      {modalReason.open ? (
        <ModalReason
          open={modalReason.open}
          typeRegister={typeRegister}
          reason={modalReason.motivo}
          yearId={yearId}
          codeEnte={codeEnte}
          progressive={progressive}
          typePractice={typePractice}
          onClose={() => {
            setOpenModalReason({ open: false, warnings: [], hasOpened: !!modalReason.motivo });
          }}
          onConfirm={() => {
            setOpenModalReason({ open: false, warnings: [], hasOpened: true });
            formContext.handleSubmit();
          }}
          warnings={modalReason.warnings}
        />
      ) : undefined}
    </Fragment>
  );
};

StepRegisterOfficeComponent.displayName = 'register office component';

const StepRegisterOffice = memo(StepRegisterOfficeComponent);

export default StepRegisterOffice;
